/**
 * What keeps the two languages' copy alike (WEB-2, card 095): the same entries, and in each entry the same arguments,
 * read from ICU message syntax as use-intl reads it. No imports, so `scripts/check-i18n.ts` runs it under Node as is.
 */
export type Copy = { readonly [key: string]: string | Copy };

/** Every entry by its path, such as `invoice.form.title`. */
export function entriesOf(copy: Copy, prefix = ""): Map<string, string> {
  const found = new Map<string, string>();
  for (const [key, value] of Object.entries(copy)) {
    const path = prefix === "" ? key : `${prefix}.${key}`;
    if (typeof value === "string") found.set(path, value);
    else for (const [inner, text] of entriesOf(value, path)) found.set(inner, text);
  }
  return found;
}

/**
 * The argument names a message uses: `{name}`, `{count, plural, ...}`, and those inside a plural's or a select's
 * branches. Text quoted with an apostrophe before a brace is literal, and `#` is the plural's own number.
 */
export function argumentsOf(message: string): string[] {
  const names = new Set<string>();
  let at = 0;
  const read = (stops: string) => {
    const start = at;
    while (at < message.length && !stops.includes(message.charAt(at))) at += 1;
    return message.slice(start, at).trim();
  };
  const text = (inBranch: boolean): void => {
    while (at < message.length) {
      const char = message.charAt(at);
      const next = message.charAt(at + 1);
      if (char === "'" && (next === "{" || next === "}")) {
        const end = message.indexOf("'", at + 1);
        at = end === -1 ? message.length : end + 1;
      } else if (char === "{") {
        at += 1;
        argument();
      } else if (char === "}" && inBranch) {
        at += 1;
        return;
      } else at += 1;
    }
  };
  const argument = (): void => {
    const name = read(",}");
    if (name !== "") names.add(name);
    if (message.charAt(at) === "}") {
      at += 1;
      return;
    }
    at += 1;
    const type = read(",}");
    if (message.charAt(at) === "}") {
      at += 1;
      return;
    }
    at += 1;
    if (type === "plural" || type === "select" || type === "selectordinal") {
      while (at < message.length) {
        read("{}");
        if (message.charAt(at) === "}") {
          at += 1;
          return;
        }
        at += 1;
        text(true);
      }
      return;
    }
    // A style, such as `{amount, number, ::currency/SGD}`: its braces are not arguments.
    for (let depth = 1; at < message.length && depth > 0; at += 1) {
      if (message.charAt(at) === "{") depth += 1;
      else if (message.charAt(at) === "}") depth -= 1;
    }
  };
  text(false);
  return [...names].sort();
}

export interface CopyFinding {
  readonly key: string;
  readonly problem: "missing" | "extra" | "arguments";
  /** For `arguments`: each language's names. */
  readonly detail?: string;
}

/** What `other` lacks, has beyond `reference`, or names differently, entry by entry. */
export function copyFindings(reference: Copy, other: Copy): CopyFinding[] {
  const wanted = entriesOf(reference);
  const given = entriesOf(other);
  const findings: CopyFinding[] = [];
  for (const [key, message] of wanted) {
    const theirs = given.get(key);
    if (theirs === undefined) {
      findings.push({ key, problem: "missing" });
      continue;
    }
    const ours = argumentsOf(message).join(", ");
    const translated = argumentsOf(theirs).join(", ");
    if (ours !== translated)
      findings.push({ key, problem: "arguments", detail: `{${ours}} against {${translated}}` });
  }
  for (const key of given.keys()) if (!wanted.has(key)) findings.push({ key, problem: "extra" });
  return findings;
}

/**
 * The reference's entries with `primary`'s where it has them: a reader of `primary` sees the reference's text for an
 * entry `primary` lacks. Returns the overlaid copy and the paths that fell back.
 */
export function overlay<T extends Copy>(reference: T, primary: Copy): { copy: T; fellBack: string[] } {
  const fellBack: string[] = [];
  const merge = (base: Copy, over: Copy | undefined, prefix: string): Copy =>
    Object.fromEntries(
      Object.entries(base).map(([key, value]) => {
        const path = prefix === "" ? key : `${prefix}.${key}`;
        const mine = over?.[key];
        if (typeof value === "string") {
          if (typeof mine === "string") return [key, mine];
          fellBack.push(path);
          return [key, value];
        }
        return [key, merge(value, typeof mine === "object" ? mine : undefined, path)];
      }),
    );
  return { copy: merge(reference, primary, "") as T, fellBack };
}
